import { Dialog, DialogContent, DialogTitle, makeStyles } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { handleMessageCenter, openMessageCenter, showAlertSnackbar } from "../../reducers/uiSlice";
import TopBar from "../Common/TopBar";
import AddIcon from '@material-ui/icons/Add';
import ConditionalSearch from "./ConditionalSearch";
import MessageDetail from "./MessageDetail";
import { getDisableOption, getDrafts, getDrivers, getMessagesGeneral, getMessagesHistory, getMessageTypes, getQuantities, getRecipientGroup, getRecipientsAll } from "../../actions/messageCenterActions";
import { getUserPreference } from "../../utilities/common";
import MessageOverview from "./MessageOverview";
import FullscreenLoading from "../Common/FullscreenLoading";

const useStyles = makeStyles((theme)=> ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
    },
    appBarClasses: {
        backgroundColor: '#424242',
        color: theme.palette.text.primary,
        padding: theme.spacing(1),
        boxShadow: 'none !important',
        paddingLeft: '0px !important',
        paddingRight: '0px !important',
        borderBottom: '1px #6e6e6e solid'
    },
    topBarButton: {
        marginLeft: theme.spacing(2),
        color: 'unset',
        '&:hover': {
            color: theme.palette.text.primary
        }
    },
    subTitle: {
        fontWeight: 700,
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(3),
        fontSize: 20
    }
}))

function MessageCenter() {
    const classes = useStyles();
    const {t} = useTranslation('messageCenter');
    const dispatch = useDispatch();
    const userPref = getUserPreference();

    const openModal = useSelector(openMessageCenter);
    const messagesGeneral = useSelector(state => state.messageCenterReducer.getMessagesGeneral);
    const messageHistory = useSelector(state => state.messageCenterReducer.getMessagesHistory);
    const drafts = useSelector(state => state.messageCenterReducer.getDrafts);


    const manageIndices = {
        messageList: {
            id: 0,
            text: t('message_list')
        },
        drafts: {
            id: 1,
            text: t('drafts')
        },
        history: {
            id: 2,
            text: t('history')
        }
    }


    const [detailOpen, setDetailOpen] = useState(false);
    const [detailType, setDetailType] = useState(false);
    const [messagesInEffective, setMessagesInEffective] = useState([]);
    const [messagesHistory, setMessagesHistory] = useState([]);
    const [messagesDrafts, setMessagesDrafts] = useState(drafts);
    const [manageIndex, setManageIndex] = useState('messageList');
    const [loading, setLoading] = useState(false);
    const [paramsSearch, setParamsSearch] = useState({});


    const handleSearch = async(params) => {
        try {
            const paramsSearchCurr = params ? params : paramsSearch;
            let res = [];
            let resModify = [];
            setLoading(true);
            if(manageIndex === 'messageList') {
                res = await dispatch(getMessagesGeneral(paramsSearchCurr));
            } else if(manageIndex === 'history') {
                res = await dispatch(getMessagesHistory(paramsSearchCurr));
            }
            if(res.length > 0) {
                await Promise.all(res.map(async(recipient) => {
                    let quantity = {};
                    if(recipient.message_type === 2) {
                        quantity = await dispatch(getQuantities(recipient.id));
                    }
                    resModify.push({
                        ...recipient,
                        unread: quantity.unread,
                        total: quantity.total,
                        recipients: recipient.recipients.toString() === '0' ? [{id: 0, name: t('common:all')}] : recipient.recipients
                    })
                }))
            }
            if(manageIndex === 'messageList') {
                await setMessagesInEffective(resModify);
            } else if(manageIndex === 'history') {
                await setMessagesHistory(resModify);
            }
        } catch(error) {
            dispatch(showAlertSnackbar({message: t('fetch_err'), type: 'error'}))
        } finally {
            setLoading(false);
            setParamsSearch(params);
        }
    }

    const handleManageIndex = (event) => {
        setManageIndex(event.target.value);
    }

    const handleClose = () => {
        dispatch(handleMessageCenter(false));
    }

    useEffect(() => {
        if(openModal) {
            dispatch(getMessageTypes());
            dispatch(getRecipientGroup());
            dispatch(getDisableOption());
            dispatch(getRecipientsAll(userPref.userCity));
        } else {
            setMessagesInEffective([]);
            setMessagesHistory([]);
        }
    }, [openModal])

    useEffect(() => {
        setMessagesDrafts(drafts);
    }, [drafts])

    useEffect(() => {
        if(manageIndex === 'drafts') {
            setLoading(true);
            dispatch(getDrafts({warehouse: userPref.userCity}))
            .catch(() => {
                dispatch(showAlertSnackbar({message: t('fetch_err'), type: 'error'}))
            })
            .finally(() => {
                setLoading(false);
            })
        }
    }, [manageIndex])

    return (
        <Dialog
            onClose={() => handleMessageCenter(false)}
            open={openModal}
            fullWidth={true}
            maxWidth='lg'
        >
            <DialogTitle>
                <TopBar
                    title={t('messages')}
                    buttons={[
                        {
                            id: 1,
                            title: t('new_message'),
                            props: {
                                startIcon: <AddIcon />,
                                onClick: ()=> {
                                    setDetailType('new');
                                    setDetailOpen(true);
                                }
                            },
                            htmlFor: undefined
                        }
                    ]}
                    buttonClasses={classes.topBarButton}
                    appBarClasses={classes.appBarClasses}
                    handleRadioChange={handleManageIndex}
                    radios={manageIndices}
                    radio={manageIndex}
                    onClose={handleClose}
                    type={'modal'}
                />
            </DialogTitle>
            <DialogContent style={{paddingBottom: 30}}>
                {
                    manageIndex !== 'drafts' &&
                    <ConditionalSearch handleSearch={handleSearch} />
                }
                {
                    manageIndex === 'messageList' && messagesInEffective?.length > 0 &&
                    <div>
                        <div className={classes.subTitle}>{t('in_effective')}</div>
                        {
                            messagesInEffective.map((message) => (
                                <MessageOverview message={message} type="effective" handleSearch={handleSearch}/>
                            ))
                        }
                    </div>
                }
                {
                    manageIndex === 'drafts' && messagesDrafts?.length > 0 &&
                    <div>
                        {
                            messagesDrafts.map((message) => (
                                <MessageOverview message={message} type="drafts" />
                            ))
                        }
                    </div>
                }
                {
                    manageIndex === 'history' && messagesHistory?.length > 0 &&
                    <div>
                        <div className={classes.subTitle}>{t('history')}</div>
                        {
                            messagesHistory.map((message) => (
                                <MessageOverview message={message} type="history" handleSearch={handleSearch}/>
                            ))
                        }
                    </div>
                }
            </DialogContent>
            <MessageDetail
                open={detailOpen}
                type={detailType}
                onClose={() => setDetailOpen(false)}
            />
            <FullscreenLoading open={loading} />
        </Dialog>
    )
}
export default MessageCenter;